import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { Star, Save, Check, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, X, Eye, CloudDownload, Plus, Trash2, Send } from 'lucide-react';

const WEEKDAY_KR = ['일', '월', '화', '수', '목', '금', '토'];
const WEEKDAY_LABELS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

function formatDateKr(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const w = WEEKDAY_KR[new Date(y, m - 1, d).getDay()];
  return `${m}.${d} (${w})`;
}

function getMonthRange(year, month) {
  const from = `${year}-${String(month).padStart(2, '0')}-01`;
  const last = new Date(year, month, 0).getDate();
  const to = `${year}-${String(month).padStart(2, '0')}-${String(last).padStart(2, '0')}`;
  return { from, to };
}

function addDays(dateStr, n) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d + n));
  return date.toISOString().slice(0, 10);
}

function getMonday(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  const day = date.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  date.setDate(date.getDate() + diff);
  const yy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

function getNextMonday(dateStr) {
  return addDays(getMonday(dateStr), 7);
}

// ─── 일별 미리보기 모달 ──────────────────────────────────────────
function PreviewModal({ releaseDate, onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [html, setHtml] = useState('');
  const [subject, setSubject] = useState('');
  const [meta, setMeta] = useState(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    axios.post('/api/promotion-email/preview', { date: releaseDate })
      .then(res => {
        setHtml(res.data.html);
        setSubject(res.data.subject);
        setMeta(res.data);
      })
      .catch(err => setError(err.response?.data?.message || '미리보기 생성 실패'))
      .finally(() => setLoading(false));
  }, [releaseDate]);

  const handleSend = async () => {
    if (!subject || !html || sending || sent) return;
    if (!window.confirm(`"${subject}"\n\n위 제목으로 플랫폼에 발송합니다. 계속하시겠습니까?`)) return;
    setSending(true);
    try {
      const res = await axios.post('/api/promotion-email/send', { subject, html });
      setSent(true);
      alert(`발송 완료 — ${res.data.recipientCount}개 플랫폼`);
    } catch (err) {
      alert(err.response?.data?.message || '발송 실패');
    }
    setSending(false);
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.75)' }}>
      <div className="bg-[#111827] border border-white/[0.1] rounded-2xl overflow-hidden flex flex-col"
           style={{ width: '760px', maxWidth: '96vw', maxHeight: '90vh' }}>
        <div className="px-5 py-4 border-b border-white/[0.06] flex items-start justify-between gap-4 shrink-0">
          <div className="min-w-0">
            <p className="text-xs text-slate-500 mb-1">이메일 미리보기</p>
            {meta && (
              <p className="text-xs text-amber-400 mb-1">
                발송일: {formatDateKr(meta.sendDate)} 오전{meta.sendTime === '08:00' ? '8' : '10'}시
                {meta.coverDates?.length > 1 && (
                  <span className="text-slate-500 ml-2">
                    ({meta.coverDates.map(formatDateKr).join(', ')} 발매 통합)
                  </span>
                )}
              </p>
            )}
            <p className="text-white text-sm font-medium truncate">{subject}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {html && !loading && (
              <button
                onClick={handleSend}
                disabled={sending || sent}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                  sent ? 'bg-emerald-500/20 text-emerald-400' :
                  sending ? 'bg-white/10 text-slate-400 cursor-not-allowed' :
                  'bg-rose-500/20 text-rose-400 hover:bg-rose-500/30'
                }`}
              >
                {sent ? '✓ 발송됨' : sending ? '발송 중...' : '지금 발송'}
              </button>
            )}
            <button onClick={onClose} className="text-slate-500 hover:text-white transition-colors mt-0.5">
              <X size={18} />
            </button>
          </div>
        </div>
        <div className="overflow-auto flex-1">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-6 h-6 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-64 text-rose-400 text-sm">{error}</div>
          ) : (
            <iframe srcDoc={html} title="email-preview" className="w-full bg-white" style={{ height: '60vh', border: 'none' }} />
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── 주간 이메일 미리보기 모달 ─────────────────────────────────────
function WeeklyPreviewModal({ targetMonday, manualEntries, onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [html, setHtml] = useState('');
  const [subject, setSubject] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    axios.post('/api/promotion-email/weekly-preview', { targetMonday, manualEntries })
      .then(res => {
        setHtml(res.data.html);
        setSubject(res.data.subject);
      })
      .catch(err => setError(err.response?.data?.message || '주간 미리보기 생성 실패'))
      .finally(() => setLoading(false));
  }, [targetMonday, manualEntries]);

  const handleSend = async () => {
    if (!subject || !html || sending || sent) return;
    if (!window.confirm(`"${subject}"\n\n위 제목으로 플랫폼에 발송합니다. 계속하시겠습니까?`)) return;
    setSending(true);
    try {
      const res = await axios.post('/api/promotion-email/send-weekly', { subject, html });
      setSent(true);
      alert(`주간 이메일 발송 완료 — ${res.data.recipientCount}개 플랫폼`);
    } catch (err) {
      alert(err.response?.data?.message || '발송 실패');
    }
    setSending(false);
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.75)' }}>
      <div className="bg-[#111827] border border-white/[0.1] rounded-2xl overflow-hidden flex flex-col"
           style={{ width: '820px', maxWidth: '96vw', maxHeight: '90vh' }}>
        <div className="px-5 py-4 border-b border-white/[0.06] flex items-start justify-between gap-4 shrink-0">
          <div className="min-w-0">
            <p className="text-xs text-slate-500 mb-1">주간 이메일 미리보기</p>
            <p className="text-xs text-amber-400 mb-1">
              발송일: 화요일 오후 12:30 (KST) · 대상주: {formatDateKr(targetMonday)} ~ {formatDateKr(addDays(targetMonday, 6))}
            </p>
            <p className="text-white text-sm font-medium truncate">{subject}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {html && !loading && (
              <button
                onClick={handleSend}
                disabled={sending || sent}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                  sent ? 'bg-emerald-500/20 text-emerald-400' :
                  sending ? 'bg-white/10 text-slate-400 cursor-not-allowed' :
                  'bg-violet-500/20 text-violet-300 hover:bg-violet-500/30'
                }`}
              >
                {sent ? '✓ 발송됨' : sending ? '발송 중...' : '지금 발송'}
              </button>
            )}
            <button onClick={onClose} className="text-slate-500 hover:text-white transition-colors mt-0.5">
              <X size={18} />
            </button>
          </div>
        </div>
        <div className="overflow-auto flex-1">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-6 h-6 border-2 border-violet-400 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-64 text-rose-400 text-sm">{error}</div>
          ) : (
            <iframe srcDoc={html} title="weekly-email-preview" className="w-full bg-white" style={{ height: '65vh', border: 'none' }} />
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── Kola 다운로드 모달 ───────────────────────────────────────────
function parseReleaseHour(timeStr) {
  if (!timeStr) return '12';
  if (timeStr.includes('오후12') || timeStr.includes('정오')) return '12';
  const pm = timeStr.match(/오후(\d+)시/);
  if (pm) return String(Number(pm[1]) + 12);
  const am = timeStr.match(/오전(\d+)시/);
  if (am) return am[1];
  return '12';
}

function KolaDownloadModal({ from, to, weekLabel, onClose }) {
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ranks, setRanks] = useState({});

  useEffect(() => {
    axios.get(`/api/promotion-email/kola?from=${from}&to=${to}`)
      .then(res => {
        setAlbums(res.data);
        const init = {};
        res.data.forEach(a => { init[a.id] = { weekly: '', daily: '1' }; });
        setRanks(init);
      })
      .finally(() => setLoading(false));
  }, [from, to]);

  const handleDownload = () => {
    const headers = ['영역1', '영역2', '음반타입', '주간순위', '일간순위', '음반LID', '음반UPC', '아티스트명', '음반명', '서비스일', '서비스시간', '노출일', '노출시간', '음반소개', '담당자 메일'];
    const rows = albums.map(a => {
      const hour = parseReleaseHour(a.promotionReleaseTime);
      return [
        '메인', '국내', a.albumType,
        String(ranks[a.id]?.weekly ?? ''), String(ranks[a.id]?.daily ?? ''),
        '', '',
        a.artistNameKr, a.nameKr,
        a.releaseDateTime, hour,
        a.releaseDateTime, hour,
        a.promotionExtra,
        a.clientEmail,
      ];
    });

    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    // 모든 셀 텍스트 서식
    const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
    for (let R = range.s.r; R <= range.e.r; R++) {
      for (let C = range.s.c; C <= range.e.c; C++) {
        const addr = XLSX.utils.encode_cell({ r: R, c: C });
        if (!ws[addr]) { ws[addr] = { t: 's', v: '' }; continue; }
        ws[addr].t = 's';
        ws[addr].v = String(ws[addr].v ?? '');
        ws[addr].z = '@';
      }
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Kola');
    XLSX.writeFile(wb, `Kola_${from.replace(/-/g, '')}_${to.replace(/-/g, '')}.xlsx`);
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.8)' }}>
      <div className="bg-[#111827] border border-white/[0.1] rounded-2xl overflow-hidden flex flex-col"
           style={{ width: '680px', maxWidth: '96vw', maxHeight: '85vh' }}>
        {/* 헤더 */}
        <div className="px-5 py-4 border-b border-white/[0.06] flex items-center justify-between shrink-0">
          <div>
            <p className="text-xs text-slate-500 mb-0.5">Kola 다운로드</p>
            <p className="text-white font-semibold text-sm">{weekLabel} 발매 앨범</p>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-white transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* 본문 */}
        <div className="overflow-auto flex-1 px-5 py-4">
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <div className="w-5 h-5 border-2 border-sky-400 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : albums.length === 0 ? (
            <p className="text-slate-500 text-sm text-center py-12">해당 기간 프로모션 앨범이 없습니다.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/[0.08]">
                  <th className="pb-2 text-left text-xs text-slate-500 font-medium pr-3">발매일</th>
                  <th className="pb-2 text-left text-xs text-slate-500 font-medium pr-3">아티스트 / 앨범</th>
                  <th className="pb-2 text-center text-xs text-slate-500 font-medium w-20">주간순위</th>
                  <th className="pb-2 text-center text-xs text-slate-500 font-medium w-20">일간순위</th>
                </tr>
              </thead>
              <tbody>
                {albums.map(a => (
                  <tr key={a.id} className="border-b border-white/[0.04]">
                    <td className="py-2.5 pr-3 text-slate-400 text-xs whitespace-nowrap tabular-nums">{a.releaseDateTime}</td>
                    <td className="py-2.5 pr-3">
                      <p className="text-white text-sm font-medium leading-tight">{a.nameKr}</p>
                      <p className="text-slate-500 text-xs">{a.artistNameKr} · {a.albumType}</p>
                    </td>
                    <td className="py-2.5 text-center">
                      <input
                        type="number"
                        min="1"
                        value={ranks[a.id]?.weekly ?? ''}
                        onChange={e => setRanks(r => ({ ...r, [a.id]: { ...r[a.id], weekly: e.target.value } }))}
                        className="w-16 bg-white/[0.07] border border-white/[0.1] rounded-lg px-2 py-1 text-center text-white text-sm focus:outline-none focus:border-sky-500/50"
                        placeholder="-"
                      />
                    </td>
                    <td className="py-2.5 text-center">
                      <input
                        type="number"
                        min="1"
                        value={ranks[a.id]?.daily ?? ''}
                        onChange={e => setRanks(r => ({ ...r, [a.id]: { ...r[a.id], daily: e.target.value } }))}
                        className="w-16 bg-white/[0.07] border border-white/[0.1] rounded-lg px-2 py-1 text-center text-white text-sm focus:outline-none focus:border-sky-500/50"
                        placeholder="-"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* 하단 */}
        {!loading && albums.length > 0 && (
          <div className="px-5 py-3 border-t border-white/[0.06] flex justify-end shrink-0">
            <button
              onClick={handleDownload}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-sky-500/20 text-sky-300 hover:bg-sky-500/30 border border-sky-500/20 transition-colors"
            >
              <CloudDownload size={15} />
              엑셀 다운로드
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

// ─── 앨범 행 ─────────────────────────────────────────────────────
function AlbumRow({ album, index, allAlbums, onSaved }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [form, setForm] = useState({
    isPromotion: album.isPromotion || false,
    isImportant: album.isImportant || false,
    promotionDesc: album.promotionDesc || '',
    promotionCredits: album.promotionCredits || '',
    promotionReleaseTime: album.promotionReleaseTime || '오후12시 (정오)',
    promotionExtra: album.promotionExtra || '',
  });

  const dirty =
    form.isPromotion !== (album.isPromotion || false) ||
    form.isImportant !== (album.isImportant || false) ||
    form.promotionDesc !== (album.promotionDesc || '') ||
    form.promotionCredits !== (album.promotionCredits || '') ||
    form.promotionReleaseTime !== (album.promotionReleaseTime || '') ||
    form.promotionExtra !== (album.promotionExtra || '');

  const handleSave = async (overrideForm) => {
    const payload = overrideForm || form;
    setSaving(true);
    try {
      await axios.put(`/api/promotion-email/albums/${album.id}`, payload);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      onSaved(album.id, payload);
    } catch (err) {
      alert(err.response?.data?.message || '저장에 실패했습니다.');
    }
    setSaving(false);
  };

  const handleTogglePromotion = async () => {
    const next = !form.isPromotion;
    const nextForm = { ...form, isPromotion: next, isImportant: next ? form.isImportant : false };
    setForm(nextForm);
    if (next && !open) setOpen(true);
    await handleSave(nextForm);
  };

  const handleToggleImportant = async () => {
    if (!form.isPromotion) return;
    const nextForm = { ...form, isImportant: !form.isImportant };
    setForm(nextForm);
    await handleSave(nextForm);
  };

  const songs = album.songs || [];
  const schedule = album.schedule || {};
  const isHoliday = schedule.sendDate && schedule.sendDate !== album.releaseDateTime;

  // 같은 메일로 나가는 앨범 = 발송일 + 발송시각이 모두 같은 것.
  // 평일 당일분(08:00)과 다음날 연휴 몰아분(10:00)은 발송일이 같아도 별개 메일이므로 시각까지 비교.
  const coAlbums = isHoliday && form.isPromotion
    ? allAlbums.filter(a =>
        a.id !== album.id &&
        a.isPromotion &&
        a.schedule?.sendDate === schedule.sendDate &&
        a.schedule?.sendTime === schedule.sendTime
      )
    : [];

  const importanceBadge = form.isPromotion
    ? form.isImportant
      ? <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400 font-medium">매우중요</span>
      : <span className="text-xs px-1.5 py-0.5 rounded bg-orange-500/20 text-orange-400 font-medium">중요</span>
    : null;

  return (
    <>
      <tr className={`border-b border-white/[0.05] transition-colors ${
        form.isPromotion ? 'bg-rose-950/20' : 'hover:bg-white/[0.02]'
      }`}>
        <td className="px-4 py-3 text-slate-500 text-sm tabular-nums text-center w-12">{index + 1}</td>

        <td className="px-3 py-3 text-slate-300 text-sm whitespace-nowrap tabular-nums w-24">
          {formatDateKr(album.releaseDateTime)}
          {isHoliday && (
            <span className="block text-xs text-amber-500/80 mt-0.5">휴일</span>
          )}
        </td>

        <td className="px-3 py-3">
          <div className="flex items-center gap-2">
            {album.image && (
              <img src={album.image} alt="" className="w-8 h-8 rounded object-cover shrink-0" />
            )}
            <div>
              <span className="text-white text-sm font-medium leading-tight block">{album.nameKr}</span>
              {importanceBadge && <div className="mt-0.5">{importanceBadge}</div>}
            </div>
          </div>
        </td>

        <td className="px-3 py-3 text-slate-400 text-sm">{album.artist?.nameKr || '-'}</td>

        <td className="px-3 py-3 text-slate-500 text-sm">{album.label || '-'}</td>

        <td className="px-3 py-3 w-32">
          {form.isPromotion ? (
            <div>
              <p className="text-xs text-amber-400 font-medium whitespace-nowrap">
                {isHoliday ? formatDateKr(schedule.sendDate) : formatDateKr(album.releaseDateTime)}
              </p>
              <p className="text-xs text-slate-600">오전{isHoliday ? '10' : (schedule.sendTime === '08:00' ? '8' : '10')}시 발송</p>
              {coAlbums.length > 0 && (
                <p className="text-xs text-rose-400 mt-0.5">+{coAlbums.length}건 통합</p>
              )}
            </div>
          ) : (
            <span className="text-slate-700 text-xs">-</span>
          )}
        </td>

        {/* 프로모션 + 중요 토글 */}
        <td className="px-3 py-3 w-28">
          <div className="flex items-center gap-2">
            <div
              onClick={handleTogglePromotion}
              className={`inline-flex relative w-10 h-5 rounded-full cursor-pointer transition-colors shrink-0 ${
                form.isPromotion ? 'bg-rose-500' : 'bg-white/20'
              }`}
            >
              <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${
                form.isPromotion ? 'translate-x-5' : 'translate-x-0.5'
              }`} />
            </div>
            {form.isPromotion && (
              <button
                onClick={handleToggleImportant}
                title={form.isImportant ? '매우중요 해제' : '매우중요로 설정'}
                className={`transition-colors ${
                  form.isImportant ? 'text-emerald-400 hover:text-emerald-300' : 'text-slate-600 hover:text-emerald-500'
                }`}
              >
                <Star size={14} fill={form.isImportant ? 'currentColor' : 'none'} />
              </button>
            )}
          </div>
        </td>

        <td className="px-3 py-3 text-center w-10">
          <button
            onClick={() => setOpen((o) => !o)}
            className="text-slate-500 hover:text-white transition-colors"
          >
            {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </td>
      </tr>

      {open && (
        <tr className={`border-b border-white/[0.08] ${form.isPromotion ? 'bg-rose-950/10' : 'bg-[#0d1117]'}`}>
          <td colSpan={8} className="px-6 py-4">
            <div className="space-y-3">
              {form.isPromotion && (
                <div className={`rounded-xl px-4 py-3 text-sm flex items-start gap-3 ${
                  isHoliday ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-white/[0.04] border border-white/[0.06]'
                }`}>
                  <div className="flex-1 min-w-0">
                    {isHoliday ? (
                      <>
                        <p className="text-amber-400 font-medium">
                          발매일({formatDateKr(album.releaseDateTime)})이 휴일입니다
                        </p>
                        <p className="text-slate-400 text-xs mt-0.5">
                          → <strong className="text-white">{formatDateKr(schedule.sendDate)} 오전10시</strong>에 발송됩니다 (휴일 직전 평일)
                          {schedule.coverDates?.length > 1 && (
                            <span className="text-slate-500">
                              {' '}(연휴 기간 {schedule.coverDates.map(formatDateKr).join(', ')} 통합)
                            </span>
                          )}
                        </p>
                        {coAlbums.length > 0 && (
                          <p className="text-xs text-rose-300 mt-1">
                            같이 발송: {coAlbums.map(a => `${a.artist?.nameKr || ''} '${a.nameKr}'`).join(', ')}
                          </p>
                        )}
                      </>
                    ) : (
                      <p className="text-slate-400 text-xs">
                        발매 당일 <strong className="text-white">{formatDateKr(album.releaseDateTime)} 오전8시</strong>에 발송됩니다
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => setShowPreview(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-white/[0.08] text-slate-300 hover:bg-white/[0.14] transition-colors shrink-0"
                  >
                    <Eye size={13} />
                    미리보기
                  </button>
                </div>
              )}

              {/* 중요도 안내 */}
              {form.isPromotion && (
                <div className="flex items-center gap-3 text-xs text-slate-500">
                  <span>중요도:</span>
                  <span className="flex items-center gap-1">
                    <span className="w-2.5 h-2.5 rounded-sm bg-orange-500 inline-block" />
                    중요 (프로모션 ON)
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-2.5 h-2.5 rounded-sm bg-emerald-500 inline-block" />
                    매우중요 (★ 별 활성화)
                  </span>
                </div>
              )}

              {songs.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {songs.map((s) => (
                    <span key={s.id} className="text-xs px-2 py-0.5 rounded bg-white/[0.06] text-slate-400">
                      {s.track ? String(s.track).padStart(2, '0') + '. ' : ''}{s.nameKr}
                    </span>
                  ))}
                </div>
              )}

              <div className="mb-3">
                <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1.5">발매시간</label>
                <div className="flex gap-2">
                  {['오후12시 (정오)', '오후6시 (18시)'].map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, promotionReleaseTime: t }))}
                      className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        form.promotionReleaseTime === t
                          ? 'bg-rose-500/30 text-rose-300 border border-rose-500/40'
                          : 'bg-white/[0.05] text-slate-400 border border-white/[0.08] hover:bg-white/[0.1]'
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-amber-400/70 uppercase tracking-wide mb-1.5">프로모션 소개 <span className="normal-case text-slate-500 font-normal">(이메일 전용 · 소개문 위 볼드 표시)</span></label>
                <textarea
                  value={form.promotionExtra}
                  onChange={(e) => setForm((f) => ({ ...f, promotionExtra: e.target.value }))}
                  placeholder="이메일에만 노출되는 홍보성 소개글..."
                  rows={3}
                  className="w-full bg-amber-500/[0.04] border border-amber-500/20 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-600 resize-none focus:outline-none focus:border-amber-500/40"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1.5">앨범 소개문</label>
                  <textarea
                    value={form.promotionDesc}
                    onChange={(e) => setForm((f) => ({ ...f, promotionDesc: e.target.value }))}
                    placeholder="앨범 소개글..."
                    rows={4}
                    className="w-full bg-white/[0.05] border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white placeholder-slate-600 resize-none focus:outline-none focus:border-rose-500/50"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1.5">크레딧</label>
                  <textarea
                    value={form.promotionCredits}
                    onChange={(e) => setForm((f) => ({ ...f, promotionCredits: e.target.value }))}
                    placeholder={"Presented by 레이블명\nExecutive Producer 제작자\nLyrics 작사가\nComposed 작곡가"}
                    rows={4}
                    className="w-full bg-white/[0.05] border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white placeholder-slate-600 resize-none focus:outline-none focus:border-rose-500/50"
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={() => handleSave()}
                  disabled={saving || (!dirty && !saved)}
                  className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    saved
                      ? 'bg-emerald-500/20 text-emerald-400'
                      : dirty
                      ? 'bg-amber-500/20 text-amber-400 hover:bg-amber-500/30'
                      : 'bg-white/[0.05] text-slate-600 cursor-not-allowed'
                  }`}
                >
                  {saved ? <Check size={14} /> : <Save size={14} />}
                  {saved ? '저장됨' : saving ? '저장 중...' : '저장'}
                </button>
              </div>
            </div>
          </td>
        </tr>
      )}

      {showPreview && (
        <PreviewModal releaseDate={album.releaseDateTime} onClose={() => setShowPreview(false)} />
      )}
    </>
  );
}

// ─── 주간 이메일 섹션 ─────────────────────────────────────────────
function WeeklyEmailSection({ refreshKey = 0 }) {
  const today = new Date();
  const todayStr = today.toISOString().slice(0, 10);
  const [targetMonday, setTargetMonday] = useState(getNextMonday(todayStr));
  // manualEntries: { 'YYYY-MM-DD': ['아티스트명\n앨범명', ...] } — localStorage로 영구 저장
  const [manualEntries, setManualEntries] = useState(() => {
    try { return JSON.parse(localStorage.getItem('weeklyManualEntries') || '{}'); } catch { return {}; }
  });
  const [addingDay, setAddingDay] = useState(null);
  const [addText, setAddText] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [showKola, setShowKola] = useState(false);
  const [dbAlbumsByDate, setDbAlbumsByDate] = useState({});
  const [loadingDb, setLoadingDb] = useState(false);

  // 발송 이력
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const fetchLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const res = await axios.get('/api/promotion-email/logs?type=weekly&limit=10');
      setLogs(res.data);
    } catch {}
    setLogsLoading(false);
  }, []);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  // manualEntries 변경 시 localStorage 동기화
  useEffect(() => {
    localStorage.setItem('weeklyManualEntries', JSON.stringify(manualEntries));
  }, [manualEntries]);

  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(targetMonday, i));

  // 대상 주 변경 시 DB 앨범 조회
  useEffect(() => {
    const from = addDays(targetMonday, -7);
    const to = addDays(targetMonday, 13);
    setLoadingDb(true);
    axios.get(`/api/promotion-email/album-list?from=${from}&to=${to}`)
      .then(res => {
        const byDate = {};
        res.data.forEach(a => {
          if (!byDate[a.releaseDateTime]) byDate[a.releaseDateTime] = [];
          byDate[a.releaseDateTime].push(a);
        });
        setDbAlbumsByDate(byDate);
      })
      .catch(() => {})
      .finally(() => setLoadingDb(false));
  }, [targetMonday, refreshKey]);

  const prevWeek = () => setTargetMonday(d => addDays(d, -7));
  const nextWeek = () => setTargetMonday(d => addDays(d, 7));

  const startAdding = (dateStr) => {
    setAddingDay(dateStr);
    setAddText('');
  };

  const confirmAdd = (dateStr) => {
    if (!addText.trim()) { setAddingDay(null); return; }
    setManualEntries(prev => ({
      ...prev,
      [dateStr]: [...(prev[dateStr] || []), addText.trim()],
    }));
    setAddText('');
    setAddingDay(null);
  };

  const removeEntry = (dateStr, idx) => {
    setManualEntries(prev => {
      const next = [...(prev[dateStr] || [])];
      next.splice(idx, 1);
      return { ...prev, [dateStr]: next };
    });
  };

  const getWeekLabel = (monday) => {
    const [y, m] = monday.split('-').map(Number);
    const firstDay = new Date(y, m - 1, 1);
    const firstMonday = getMonday(monday.slice(0, 8) + '01');
    const diff = Math.round((new Date(monday) - new Date(firstMonday)) / (7 * 86400000));
    return `${m}월 ${diff + 1}주`;
  };

  const mondayInfo = formatDateKr(targetMonday);
  const sundayInfo = formatDateKr(addDays(targetMonday, 6));

  return (
    <div className="space-y-4">
    <div className="bg-[#111827] border border-white/[0.08] rounded-2xl overflow-hidden">
      {/* 헤더 */}
      <div className="px-5 py-4 border-b border-white/[0.06] flex items-center justify-between">
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold mb-0.5">주간 프로모션 이메일</p>
          <p className="text-xs text-slate-600">매주 화요일 오후 12:30 자동 발송 · 차주 발매 앨범 일정 안내</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={prevWeek} className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/[0.08] transition-colors">
            <ChevronLeft size={16} />
          </button>
          <div className="text-center">
            <p className="text-white font-bold text-sm">{getWeekLabel(targetMonday)}</p>
            <p className="text-xs text-slate-500">{mondayInfo} ~ {sundayInfo}</p>
          </div>
          <button onClick={nextWeek} className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/[0.08] transition-colors">
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {/* 안내 */}
      <div className="px-5 pt-3 pb-1 flex items-center gap-4 text-xs text-slate-500">
        <span>캘린더에 수동으로 일정 추가 가능 (이메일 발송 시에만 사용)</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-orange-500 inline-block" />중요</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-500 inline-block" />매우중요</span>
      </div>

      {/* 7일 캘린더 카드 */}
      <div className="px-5 py-4 grid grid-cols-7 gap-2">
        {weekDays.map((dateStr, i) => {
          const [, m, d] = dateStr.split('-').map(Number);
          const isWeekend = i >= 5;
          const entries = manualEntries[dateStr] || [];
          const dbAlbums = dbAlbumsByDate[dateStr] || [];
          const isAdding = addingDay === dateStr;
          return (
            <div
              key={dateStr}
              className={`rounded-xl border p-2 min-h-[100px] flex flex-col ${
                isWeekend
                  ? 'border-rose-900/30 bg-rose-950/10'
                  : 'border-white/[0.06] bg-white/[0.02]'
              }`}
            >
              <div className={`text-xs font-bold mb-1.5 ${isWeekend ? 'text-rose-400' : 'text-slate-300'}`}>
                <span className="block text-slate-500 font-medium text-[10px]">{WEEKDAY_LABELS[i]}</span>
                {m}.{d}
              </div>
              <div className="flex-1 space-y-1">
                {/* DB 앨범 (읽기 전용) */}
                {dbAlbums.map(a => {
                  const isImportant = a.isImportant;
                  const isPromo = a.isPromotion;
                  const borderColor = isImportant ? '#2d6a4f' : isPromo ? '#c87941' : '#444';
                  const bgOpacity = isImportant ? 'bg-emerald-950/40' : isPromo ? 'bg-orange-950/40' : 'bg-white/[0.04]';
                  return (
                    <div key={a.id} className={`${bgOpacity} rounded px-1.5 py-1`} style={{ borderLeft: `3px solid ${borderColor}` }}>
                      <div className="text-[10px] leading-snug text-slate-300">
                        <span className="font-semibold block truncate">{a.artist?.nameKr || ''}</span>
                        <span className="text-slate-500 block truncate">{a.nameKr}</span>
                      </div>
                    </div>
                  );
                })}
                {entries.map((text, idx) => {
                  const lines = text.split('\n');
                  return (
                    <div key={idx} className="group flex items-start gap-1 bg-white/[0.06] rounded px-1.5 py-1">
                      <div className="flex-1 min-w-0 text-[10px] leading-snug text-slate-300">
                        {lines[0] && <span className="font-semibold block truncate">{lines[0]}</span>}
                        {lines[1] && <span className="text-slate-500 block truncate">{lines[1]}</span>}
                      </div>
                      <button
                        onClick={() => removeEntry(dateStr, idx)}
                        className="opacity-0 group-hover:opacity-100 text-slate-600 hover:text-rose-400 transition-all shrink-0 mt-0.5"
                      >
                        <X size={10} />
                      </button>
                    </div>
                  );
                })}
                {isAdding ? (
                  <div className="space-y-1">
                    <textarea
                      autoFocus
                      value={addText}
                      onChange={e => setAddText(e.target.value)}
                      placeholder={"아티스트명\n앨범명"}
                      rows={2}
                      className="w-full bg-white/[0.08] border border-white/[0.12] rounded px-1.5 py-1 text-[10px] text-white placeholder-slate-600 resize-none focus:outline-none focus:border-violet-500/50"
                      onKeyDown={e => {
                        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); confirmAdd(dateStr); }
                        if (e.key === 'Escape') setAddingDay(null);
                      }}
                    />
                    <div className="flex gap-1">
                      <button
                        onClick={() => confirmAdd(dateStr)}
                        className="flex-1 py-0.5 rounded text-[10px] bg-violet-500/20 text-violet-300 hover:bg-violet-500/30 transition-colors"
                      >
                        추가
                      </button>
                      <button
                        onClick={() => setAddingDay(null)}
                        className="flex-1 py-0.5 rounded text-[10px] bg-white/[0.06] text-slate-500 hover:bg-white/[0.1] transition-colors"
                      >
                        취소
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => startAdding(dateStr)}
                    className="w-full py-1 rounded text-[10px] text-slate-600 hover:text-slate-400 hover:bg-white/[0.06] transition-colors flex items-center justify-center gap-0.5 border border-dashed border-white/[0.08]"
                  >
                    <Plus size={9} />
                    추가
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* 하단 버튼 */}
      <div className="px-5 py-4 border-t border-white/[0.06] flex items-center justify-end gap-3">
        <p className="text-xs text-slate-600 flex-1">
          DB의 프로모션 설정 앨범{loadingDb ? ' (불러오는 중...)' : ''} + 수동 입력 항목이 합쳐져 이메일로 발송됩니다.
        </p>
        <button
          onClick={() => setShowKola(true)}
          className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium bg-sky-500/15 text-sky-300 hover:bg-sky-500/25 border border-sky-500/20 transition-colors"
        >
          <CloudDownload size={14} />
          Kola 다운로드
        </button>
        <button
          onClick={() => setShowPreview(true)}
          className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium bg-violet-500/15 text-violet-300 hover:bg-violet-500/25 border border-violet-500/20 transition-colors"
        >
          <Eye size={14} />
          미리보기 및 발송
        </button>
      </div>

      {showPreview && (
        <WeeklyPreviewModal
          targetMonday={targetMonday}
          manualEntries={manualEntries}
          onClose={() => setShowPreview(false)}
        />
      )}
      {showKola && (
        <KolaDownloadModal
          from={targetMonday}
          to={addDays(targetMonday, 6)}
          weekLabel={getWeekLabel(targetMonday)}
          onClose={() => setShowKola(false)}
        />
      )}
    </div>

      {/* 최근 발송 이력 */}
      <div className="bg-[#111827] border border-white/[0.08] rounded-2xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white text-sm font-bold">최근 발송 이력</h3>
          <button
            onClick={fetchLogs}
            className="text-xs text-slate-400 hover:text-white transition-colors"
          >
            새로고침
          </button>
        </div>
        {logsLoading ? (
          <p className="text-xs text-slate-500 text-center py-4">불러오는 중...</p>
        ) : logs.length === 0 ? (
          <p className="text-xs text-slate-600 text-center py-4">발송 이력이 없습니다.</p>
        ) : (
          <div className="space-y-3">
            {logs.map(log => (
              <div key={log.id} className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4">
                <p className="text-white text-xs font-medium truncate">{log.subject}</p>
                <p className="text-slate-500 text-xs mt-0.5">
                  {new Date(log.sentAt).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' })}
                  <span className="ml-2 text-slate-600">{log.recipientCount}명 발송</span>
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

    </div>
  );
}

// ─── 메인 페이지 ─────────────────────────────────────────────────
export default function AdminPromotionManagePage() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchAlbums = useCallback(async () => {
    setLoading(true);
    setError('');
    const { from, to } = getMonthRange(year, month);
    try {
      const res = await axios.get(`/api/promotion-email/album-list?from=${from}&to=${to}`);
      const sorted = [...res.data].sort((a, b) => {
        if (a.releaseDateTime < b.releaseDateTime) return -1;
        if (a.releaseDateTime > b.releaseDateTime) return 1;
        return Number(parseReleaseHour(a.promotionReleaseTime)) - Number(parseReleaseHour(b.promotionReleaseTime));
      });
      setAlbums(sorted);
    } catch (err) {
      setError(err.response?.data?.message || '앨범을 불러오지 못했습니다.');
      setAlbums([]);
    }
    setLoading(false);
  }, [year, month]);

  useEffect(() => { fetchAlbums(); }, [fetchAlbums]);

  const handleAlbumSaved = (albumId, form) => {
    setAlbums((prev) => {
      const updated = prev.map((a) => a.id === albumId ? { ...a, ...form } : a);
      return [...updated].sort((a, b) => {
        if (a.releaseDateTime < b.releaseDateTime) return -1;
        if (a.releaseDateTime > b.releaseDateTime) return 1;
        return Number(parseReleaseHour(a.promotionReleaseTime)) - Number(parseReleaseHour(b.promotionReleaseTime));
      });
    });
  };

  const prevMonth = () => {
    if (month === 1) { setYear(y => y - 1); setMonth(12); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 12) { setYear(y => y + 1); setMonth(1); }
    else setMonth(m => m + 1);
  };

  const promoCount = albums.filter((a) => a.isPromotion).length;
  const importantCount = albums.filter((a) => a.isImportant).length;

  const [driveSync, setDriveSync] = useState({ loading: false, result: null });
  const [weeklyKey, setWeeklyKey] = useState(0);

  const handleDriveSync = async () => {
    if (driveSync.loading) return;
    setDriveSync({ loading: true, result: null });
    const { from } = getMonthRange(year, month);
    try {
      const res = await axios.post('/api/drive-import/sync-all', { sinceDate: from });
      const results = Array.isArray(res.data) ? res.data : [res.data];
      const created = results.filter(r => r.isNew).length;
      const updated = results.filter(r => !r.isNew && !r.skipped && !r.error).length;
      // 스킵된 폴더는 사유까지 보여줘야 "동기화가 안 된다"는 혼동이 없음 (예: 메타 엑셀 미업로드)
      const skippedList = results
        .filter(r => r.skipped)
        .map(r => `${r.folder} — ${r.reason}`);
      setDriveSync({ loading: false, result: { created, updated, errors: results.filter(r => r.error).length, total: results.length, skippedList } });
      if (created > 0 || updated > 0) { fetchAlbums(); setWeeklyKey(k => k + 1); }
    } catch (e) {
      setDriveSync({ loading: false, result: { error: e.response?.data?.message || e.message } });
    }
    setTimeout(() => setDriveSync(s => ({ ...s, result: null })), 20000);
  };

  return (
    <div className="space-y-6">
      <div className="flex mb-8 justify-center items-center">
        <h1 className="text-xl sm:text-2xl font-bold text-white tracking-tight">프로모션 발매 관리</h1>
      </div>

      {/* 일별 발매 테이블 */}
      <div className="bg-[#111827] border border-white/[0.08] rounded-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-white/[0.06] flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1">
              <button onClick={prevMonth} className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/[0.08] transition-colors">
                <ChevronLeft size={16} />
              </button>
              <span className="text-white font-bold text-lg w-24 text-center tabular-nums">
                {year}.{String(month).padStart(2, '0')}
              </span>
              <button onClick={nextMonth} className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/[0.08] transition-colors">
                <ChevronRight size={16} />
              </button>
            </div>
            <div className="text-slate-500 text-sm">
              전체 {albums.length}개
              {promoCount > 0 && (
                <span className="text-orange-400 ml-2 font-medium">중요 {promoCount}개</span>
              )}
              {importantCount > 0 && (
                <span className="text-emerald-400 ml-2 font-medium">★ 매우중요 {importantCount}개</span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {driveSync.result && !driveSync.result.error && (
              <span className="text-xs text-emerald-400">
                신규 {driveSync.result.created}건 / 업데이트 {driveSync.result.updated}건
                {driveSync.result.errors > 0 && <span className="text-rose-400 ml-1">오류 {driveSync.result.errors}건</span>}
                {driveSync.result.skippedList?.length > 0 && (
                  <span className="text-amber-400 ml-1">스킵 {driveSync.result.skippedList.length}건</span>
                )}
              </span>
            )}
            {driveSync.result?.error && (
              <span className="text-xs text-rose-400">{driveSync.result.error}</span>
            )}
            <button
              onClick={handleDriveSync}
              disabled={driveSync.loading}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-white/[0.06] text-slate-300 hover:bg-white/[0.1] transition-colors disabled:opacity-50"
              title="구글 드라이브에서 발매 데이터 자동 임포트"
            >
              <CloudDownload size={13} className={driveSync.loading ? 'animate-pulse' : ''} />
              {driveSync.loading ? '동기화 중...' : '드라이브 동기화'}
            </button>
          </div>
        </div>

        {driveSync.result?.skippedList?.length > 0 && (
          <div className="mx-5 mt-3 rounded-xl bg-amber-500/10 border border-amber-500/20 px-4 py-3">
            <p className="text-xs font-semibold text-amber-400 mb-1">동기화에서 제외된 폴더 (등록되지 않음)</p>
            {driveSync.result.skippedList.map((s, i) => (
              <p key={i} className="text-xs text-amber-300/80">• {s}</p>
            ))}
            <p className="text-[11px] text-slate-500 mt-1.5">메타 엑셀(meta.xlsx) 등 필수 파일을 폴더에 올린 뒤 다시 동기화하면 등록됩니다.</p>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/[0.06]">
                <th className="px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide text-center">#</th>
                <th className="px-3 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide text-left">날짜</th>
                <th className="px-3 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide text-left">앨범명</th>
                <th className="px-3 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide text-left">아티스트</th>
                <th className="px-3 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide text-left">기획사</th>
                <th className="px-3 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide text-left">발송 예정</th>
                <th className="px-3 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide text-center">프로모션 / 중요</th>
                <th className="w-10" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="text-center py-16 text-slate-500 text-sm">불러오는 중...</td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={8} className="text-center py-16 text-rose-400 text-sm">{error}</td>
                </tr>
              ) : albums.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-16 text-slate-500 text-sm">해당 기간에 발매 앨범이 없습니다.</td>
                </tr>
              ) : (
                albums.map((album, i) => (
                  <AlbumRow
                    key={album.id}
                    album={album}
                    index={i}
                    allAlbums={albums}
                    onSaved={handleAlbumSaved}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 주간 이메일 섹션 */}
      <WeeklyEmailSection refreshKey={weeklyKey} />
    </div>
  );
}
